<script setup>
// 미디어 허브 기사 페이지 — /media/:homepageContentCode
//
// 2026-10-07 어두운 시안에 맞춰 다시 짰다. 전에는 흰 바탕에 제 미니 헤더를 달고
// 있어서, 새로 갈아엎은 메인·목록에서 카드를 누르면 전혀 다른 화면으로 떨어졌다.
//
// ── 이 화면에는 시안이 없다 ─────────────────────────────────────────
// 새 시안 네 장은 B2B(10004:211) · 자사몰 메인(10004:789) ·
// 미디어허브 메인(10004:1741) · 미디어허브 "상세"(10004:1370) 인데,
// 그 "상세"는 이 화면이 아니라 **목록 화면**이다(제목 + 검색 + 칩 + 총 N개 + 카드 격자).
// 그래서 여기는 공용 헤더·푸터와 목록/메인이 쓰는 값으로 맞췄다.
//   바탕 #121212 · 칩 #ff9401 13~16 · 제목 40/50 Bold · 출처 알약 흰색 10% 모서리 5
//   본문 칸 1490(목록과 같다) · 카드 MediaCardDark 4열
//
// ── 본문은 못 보여 준다 ─────────────────────────────────────────────
// 상세 API 가 제목·출처·날짜·원문주소만 준다. description 과 thumbnailUrl 은
// 지금 데이터에서 빈 값이다. 그래서 이 화면은 "원문으로 건너가는 자리"다 —
// 큰 그림 한 장과 바로가기, 그리고 같은 카테고리의 다른 글을 둔다.
// 서버가 본문을 내려 주게 되면 제목 아래에 그대로 끼우면 된다.
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import SiteHeader from '@/components/common/SiteHeader.vue'
import MediaCardDark from '@/components/media3/MediaCardDark.vue'
import MediaFooter from '@/components/media2/MediaFooter.vue'
import { FALLBACK_THUMB, formatDate, sourceLabel } from '@/composables/useMediaCards.js'
import {
  getMediaHubCategoryContents,
  getMediaHubContent,
  getMediaHubContentGoUrl,
} from '@/api/mediaHub'
import imgGlow from '@/assets/figma/mh3/glow.svg'
import imgSourceDot from '@/assets/figma/mh3/src-dot-hero.svg'
import imgArrowMore from '@/assets/figma/mh3/arrow-more.svg'

const props = defineProps({
  homepageContentCode: { type: String, required: true },
})

const router = useRouter()

// ── 반응형 배율 ─────────────────────────────────────────────────────
// 메인·목록과 같은 규칙이다. 1920 으로 그려진 시안을 창 폭에 맞춰 통째로 줄인다.
const DESIGN_WIDTH = 1920
// 배율 하한. 2026-10-14 에 1188 -> 1440 으로 올렸다 - 1440 아래는 설계 대상이
// 아니다(지시). 그 아래에서는 더 줄이지 않고 좌우 여백만 깎인다. 가로 스크롤은
// 생기지 않는다 - 레이아웃이 max-w + w-full 이라 1920 이 딱딱한 폭이 아니다.
const MIN_VIEWPORT = 1440
// 배율 기준. 2026-10-14 에 DESIGN_WIDTH(1920) 에서 MIN_VIEWPORT(1440) 로 옮겼다 -
// "1440x900 에서 배율은 1 이어야 한다" 는 지시다. 하한도 1440 이라 결과적으로
// 배율은 늘 1 이고, 설계 px 가 곧 화면 px 가 된다(글자가 더 줄지 않는다).
// DESIGN_WIDTH 는 좌표계(1920) 로 그대로 쓴다 - 그림 층이 1920 좌표다.
// 되돌리려면 ZOOM_BASIS 를 DESIGN_WIDTH 로 바꾸면 된다.
const ZOOM_BASIS = MIN_VIEWPORT
const ZOOM_FLOOR = MIN_VIEWPORT / ZOOM_BASIS
const zoom = ref(1)
const measureZoom = () => {
  const w = window.innerWidth
  if (!w) return
  zoom.value = Math.min(1, Math.max(ZOOM_FLOOR, w / ZOOM_BASIS))
}

// ── 상태 ─────────────────────────────────────────────────────────────
const content = ref(null)
const related = ref([])
const loading = ref(false)
const failed = ref(false)

const RELATED_MAX = 4

const badge = computed(() => (content.value ? sourceLabel(content.value) : ''))
const date = computed(() =>
  content.value ? formatDate(content.value.publishedDt || content.value.fetchedDt) : '',
)
const author = computed(
  () =>
    content.value?.authorName ||
    content.value?.mediaSourceName ||
    content.value?.mediaHubCategoryName ||
    '',
)
const thumb = computed(() => content.value?.thumbnailUrl || FALLBACK_THUMB)

// 원문으로 갈 때는 서버의 /go 를 거친다 — 거기서 조회수가 올라간다(302 로 원문에 보낸다).
// originalUrl 로 바로 가면 통계가 안 잡힌다.
const goUrl = computed(() =>
  content.value?.homepageContentCode
    ? getMediaHubContentGoUrl(content.value.homepageContentCode)
    : content.value?.originalUrl || '',
)
const categoryName = computed(() => content.value?.displayCategoryName || '')
const categoryCode = computed(() => content.value?.mediaHubDisplayCategoryCode || '')

const onThumbError = (e) => {
  if (e.target.src.endsWith(FALLBACK_THUMB)) return
  e.target.src = FALLBACK_THUMB
}

// ── 이동 ─────────────────────────────────────────────────────────────
const openItem = (item) => {
  if (item?.homepageContentCode) router.push(`/media/${item.homepageContentCode}`)
}
const openCategory = () =>
  router.push(
    categoryCode.value
      ? { name: 'media-category-new', params: { categoryCode: categoryCode.value } }
      : { name: 'media-list-new' },
  )

// ── 불러오기 ─────────────────────────────────────────────────────────
const loadRelated = async (code, exclude) => {
  if (!code) {
    related.value = []
    return
  }
  try {
    // 자기 자신이 섞여 나오므로 한 장 더 받아서 걸러 낸다.
    const data = await getMediaHubCategoryContents(code, {
      page: 1,
      size: RELATED_MAX + 1,
      sort: 'latest',
    })
    related.value = (data.items || [])
      .filter((x) => x.homepageContentCode !== exclude)
      .slice(0, RELATED_MAX)
  } catch (e) {
    console.warn('[MediaDetail] 같은 카테고리 목록 로드 실패:', e)
    related.value = []
  }
}

const load = async (code) => {
  loading.value = true
  failed.value = false
  content.value = null
  related.value = []
  try {
    content.value = await getMediaHubContent(code)
    await loadRelated(content.value?.mediaHubDisplayCategoryCode, code)
  } catch (e) {
    console.warn('[MediaDetail] 콘텐츠 로드 실패:', e)
    failed.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  measureZoom()
  window.addEventListener('resize', measureZoom)
  load(props.homepageContentCode)
})

// 같은 화면에서 '다른 글'을 누르면 주소만 바뀌고 다시 안 그려진다.
watch(
  () => props.homepageContentCode,
  (code) => {
    if (!code) return
    window.scrollTo({ top: 0 })
    load(code)
  },
)
</script>

<template>
  <div
    class="relative min-h-screen w-full overflow-hidden bg-[#121212]"
    :style="{ zoom, '--z': zoom }"
  >
    <!-- 왼쪽 위에서 번지는 빛. 목록 화면과 같은 판이다. -->
    <div
      aria-hidden
      class="pointer-events-none absolute left-[-294px] top-[-451px] size-[1147px]"
    >
      <div class="absolute inset-[-34.87%]">
        <img alt="" class="block size-full max-w-none" :src="imgGlow" />
      </div>
    </div>

    <SiteHeader active="media" :show-rates="false" />

    <div class="relative mx-auto w-full max-w-[1530px] px-[20px] pb-[200px] pt-[70px]">
      <p v-if="loading" class="py-[120px] text-[16px] font-medium leading-[24px] text-[#7c7e83]">
        콘텐츠를 불러오고 있습니다.
      </p>

      <p v-else-if="failed || !content" class="py-[120px] text-[16px] font-medium leading-[24px] text-[#7c7e83]">
        콘텐츠를 불러오지 못했습니다.
      </p>

      <article v-else>
        <!-- 카테고리 · 날짜 -->
        <div class="flex items-center gap-[10px] whitespace-nowrap text-[16px] not-italic leading-[20px]">
          <span v-if="badge" class="shrink-0 font-semibold text-[#ff9401]">{{ badge }}</span>
          <span v-if="date" class="shrink-0 font-normal text-[#7c7e83]">{{ date }}</span>
        </div>

        <h1 class="mt-[20px] max-w-[1100px] text-[40px] font-bold not-italic leading-[50px] text-white">
          {{ content.title }}
        </h1>

        <!-- 출처 알약. 메인 히어로와 같은 모양이다. -->
        <div
          v-if="author"
          class="mt-[25px] inline-flex items-center gap-[5px] rounded-[5px] bg-white/10 py-[8px] pl-[8px] pr-[10px]"
        >
          <img alt="" class="block size-[18px] max-w-none shrink-0" :src="imgSourceDot" />
          <span class="shrink-0 whitespace-nowrap text-[13px] font-semibold not-italic leading-[18px] text-white">
            {{ author }}
          </span>
        </div>

        <!-- 큰 그림 한 장. 본문을 못 받으므로 이게 이 화면의 몸통이다. -->
        <div class="mt-[50px] aspect-[16/9] w-full max-w-[1100px] overflow-hidden rounded-[20px] bg-[#1c1c1c]">
          <img :src="thumb" :alt="content.title || ''" class="size-full object-cover" @error="onThumbError" />
        </div>

        <!-- 서버가 본문을 내려 주면 여기에 그대로 들어온다. 지금 데이터는 늘 빈 값이다. -->
        <p
          v-if="content.description"
          class="mt-[50px] max-w-[1100px] whitespace-pre-line text-[18px] font-medium leading-[32px] text-white/80"
        >
          {{ content.description }}
        </p>

        <div class="mt-[50px] flex flex-wrap items-center gap-[15px]">
          <a
            v-if="goUrl"
            :href="goUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="md-cta md-cta-on"
          >
            원문 보러 가기
            <img alt="" class="block size-[20px] shrink-0" :src="imgArrowMore" />
          </a>
          <button type="button" class="md-cta md-cta-off" @click="openCategory">
            {{ categoryName ? `${categoryName} 전체보기` : '목록으로' }}
          </button>
        </div>

        <!-- 같은 카테고리의 다른 글 -->
        <template v-if="related.length">
          <div class="mt-[100px] h-px w-full bg-white/20" />
          <h2 class="mt-[100px] text-[32px] font-bold not-italic leading-normal text-white">
            {{ categoryName ? `${categoryName}의 다른 콘텐츠` : '다른 콘텐츠' }}
          </h2>
          <div class="mt-[30px] grid grid-cols-4 gap-x-[30px] gap-y-[100px]">
            <button
              v-for="item in related"
              :key="item.homepageContentCode || item.title"
              type="button"
              class="block w-full cursor-pointer text-left"
              @click="openItem(item)"
            >
              <MediaCardDark :item="item" />
            </button>
          </div>
        </template>
      </article>
    </div>

    <MediaFooter />
  </div>
</template>

<style scoped>
/* 버튼 둘. 켠 쪽은 목록 화면의 고른 칩과 같은 흰 알약이고,
   끈 쪽은 흰 테두리만 남긴다. */
.md-cta {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 150ms ease,
    color 150ms ease;
}
.md-cta-on {
  background: #fff;
  color: #000;
}
.md-cta-on:hover {
  background: #e6e6e6;
}
.md-cta-off {
  background: transparent;
  color: #fff;
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 20%);
}
.md-cta-off:hover {
  background: rgb(255 255 255 / 10%);
}
</style>
